import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Spin, notification } from 'antd';
import moment from 'moment';
import logger, { Logger } from '../../utils/logger';
import { checkGoogleTokenStatus, getGoogleTokens, disconnectGoogleScope } from '../../services/integrations';

interface GoogleMailIntegrationProps {
  userId: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const GoogleMailIntegration: React.FC<GoogleMailIntegrationProps> = ({
  userId,
  onSuccess,
  onError,
}) => {
  logger.log("🚀 GMAIL COMPONENT: Component mounting...");
  logger.log("👤 GMAIL COMPONENT: User ID:", userId);
  
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedDate, setConnectedDate] = useState<Date | null>(null);

  const checkMailStatus = async () => {
    logger.log("🔍 GMAIL COMPONENT: Starting Gmail status check...");
    try {
      logger.log("📡 GMAIL COMPONENT: Calling checkGoogleTokenStatus service...");
      const response = await checkGoogleTokenStatus();
      logger.log("✅ GMAIL COMPONENT: Service response received:", response.data);
      
      const data = response.data;
      Logger.log('Gmail Status Response:', data);

      if (data.status && data.data) {
        logger.log("📋 GMAIL COMPONENT: Checking Gmail scope in response...");
        const hasMailScope = data.data.scope?.includes('https://www.googleapis.com/auth/gmail.send');
        const isActive = data.data.status === 'active' && hasMailScope;
        
        logger.log("🔍 GMAIL COMPONENT: Scope analysis:", {
          fullScope: data.data.scope,
          hasMailScope,
          status: data.data.status,
          isActive
        });
        
        setIsConnected(isActive);
        
        if (isActive && data.data.connectedAt) {
          logger.log("📅 GMAIL COMPONENT: Setting connected date:", data.data.connectedAt);
          setConnectedDate(new Date(data.data.connectedAt));
        }
        
        logger.log("✅ GMAIL COMPONENT: Gmail is", isActive ? "CONNECTED" : "NOT CONNECTED");
      } else {
        logger.log("❌ GMAIL COMPONENT: Invalid response structure");
        setIsConnected(false);
      }
    } catch (error) {
      logger.error('💥 GMAIL COMPONENT: Error checking mail status:', error);
      setIsConnected(false);
    } finally {
      logger.log("🔄 GMAIL COMPONENT: Setting loading to false");
      setLoading(false);
    }
  };

  useEffect(() => {
    logger.log("🔄 GMAIL COMPONENT: useEffect triggered - starting status check");
    checkMailStatus();
  }, []);

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/gmail.send',
    onSuccess: async (codeResponse) => {
      logger.log("🎉 GMAIL COMPONENT: Google OAuth success!");
      logger.log("🔑 GMAIL COMPONENT: Authorization code received:", codeResponse.code?.substring(0, 20) + "...");
      
      try {
        setLoading(true);
        logger.log("📡 GMAIL COMPONENT: Calling getGoogleTokens service...");
        
        const response = await getGoogleTokens(codeResponse.code);
        logger.log("✅ GMAIL COMPONENT: Token exchange response:", response.data);
        
        const data = response.data;
        if (data.status) {
          logger.log("🔄 GMAIL COMPONENT: Token exchange successful, rechecking status...");
          await checkMailStatus();
          
          notification.success({
            message: 'Gmail connected successfully!'
          });
          logger.log("🎉 GMAIL COMPONENT: Calling onSuccess callback");
          onSuccess();
          setConnectedDate(new Date());
        } else {
          logger.log("❌ GMAIL COMPONENT: Token exchange failed:", data);
        }
      } catch (error) {
        logger.error('💥 GMAIL COMPONENT: Google login failed:', error);
        notification.error({
          message: 'Connection failed'
        });
        onError('Connection failed');
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      logger.error('💥 GMAIL COMPONENT: Google OAuth error:', error);
    }
  });

  const handleDisconnect = async () => {
    logger.log("🔌 GMAIL COMPONENT: Starting disconnect process...");
    try {
      setLoading(true);
      logger.log("📡 GMAIL COMPONENT: Calling disconnectGoogleScope service...");
      
      const response = await disconnectGoogleScope('https://www.googleapis.com/auth/gmail.send');
      logger.log("✅ GMAIL COMPONENT: Disconnect response:", response.data);
      
      const data = response.data;
      if (data.status) {
        logger.log("✅ GMAIL COMPONENT: Disconnect successful");
        setIsConnected(false);
        setConnectedDate(null);
        
        notification.success({
          message: 'Gmail disconnected successfully!'
        });
        logger.log("🎉 GMAIL COMPONENT: Calling onSuccess callback");
        onSuccess();
      } else {
        logger.log("❌ GMAIL COMPONENT: Disconnect failed:", data);
      }
    } catch (error) {
      logger.error('💥 GMAIL COMPONENT: Disconnect failed:', error);
      notification.error({
        message: 'Failed to disconnect Gmail'
      });
      onError('Disconnect failed');
    } finally {
      setLoading(false);
    }
  };

  logger.log("🎨 GMAIL COMPONENT: Rendering with state:", { loading, isConnected, connectedDate });

  return (
    <div style={{ 
      width: '100%',
      maxWidth: '600px',
      margin: '40px auto',
      padding: '30px',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      textAlign: 'center'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '30px'
      }}>
        <img 
          src={require('../../assets/images/integration/gmail.png')}
          alt="Gmail"
          style={{
            width: '28px',
            height: '28px',
            marginRight: '15px'
          }}
        />
        <h2 style={{ 
          margin: 0,
          fontSize: '24px',
          color: '#333'
        }}>
          Gmail
        </h2>
      </div>

      {loading ? (
        <div>
          <Spin />
          <p style={{ marginTop: '20px', color: '#666' }}>
            Checking Gmail connection status...
          </p>
        </div>
      ) : isConnected ? (
        <div>
          <div style={{ 
            marginBottom: '20px', 
            color: '#52c41a',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#52c41a' }}>✓</span>
            Your Gmail account is connected and working properly
          </div>
          
          <p style={{ 
            color: '#666',
            fontSize: '15px',
            marginBottom: '20px'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              textAlign: 'left',
              margin: '0 auto',
              maxWidth: '400px'
            }}>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#EA4335', marginRight: '10px' }}>•</span>
                Send personalized email campaigns through Gmail
              </li>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#EA4335', marginRight: '10px' }}>•</span>
                Auto-sync all your emails with 3sigma CRM
              </li>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#EA4335', marginRight: '10px' }}>•</span>
                Use Gmail in your automation workflows
              </li>
            </ul>
          </p>
          
          {connectedDate && (
            <p style={{ 
              color: '#666',
              fontSize: '14px',
              marginTop: '20px'
            }}>
              Gmail connected successfully on {moment(connectedDate).format("DD MMM YYYY hh:mm A")}
            </p>
          )}
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              onClick={handleDisconnect}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: '#EA4335',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Disconnect Gmail
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p style={{ 
            marginBottom: '25px',
            color: '#666',
            fontSize: '15px'
          }}>
            Connect Gmail to 3sigma CRM to sync all your emails, send personalized campaigns, and automate email communications - all from your Gmail account.
          </p>
          
          <button
            onClick={() => {
              logger.log("🔘 GMAIL COMPONENT: Connect button clicked");
              googleLogin();
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: '#EA4335',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
          >
            Connect Gmail
          </button>
        </div>
      )}
    </div>
  );
};

export default GoogleMailIntegration;